<template>
  <div
    class="play-select"
    :class="[`play-select--${size}`, { 'is-disabled': disabled, 'is-error': !!error }]"
  >
    <select
      :id="id || null"
      class="play-select__field"
      :value="value"
      :disabled="disabled"
      :aria-invalid="!!error"
      v-bind="$attrs"
      v-on="fieldListeners"
    >
      <option
        v-if="placeholder"
        value=""
        disabled
      >
        {{ placeholder }}
      </option>
      <option
        v-for="(opt, i) in normalizedOptions"
        :key="i"
        :value="opt.value"
        :disabled="opt.disabled"
      >
        {{ opt.text }}
      </option>
      <slot />
    </select>
    <span
      class="play-select__caret"
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
    </span>
  </div>
</template>

<script>
/**
 * PlaySelect — native <select> styled for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-form-select>. Drive it with v-model; `options` accepts the b-form-select
 * shape — plain strings or `{ value, text, disabled }` — and a custom caret
 * replaces the native arrow. Wrap it in <PlayFormGroup> for a label/error.
 *
 * VUE3-MIGRATION: two Vue 2-isms, both isolated:
 *   1. `model: { prop: 'value', event: 'change' }` — Vue 3 uses
 *      `modelValue` / `update:modelValue`. Change this block + the emit name.
 *   2. `fieldListeners` spreads `$listeners` (removed in Vue 3, merged into
 *      $attrs). On migration drop the spread; keep the local change handler.
 */
export default {
  name: 'PlaySelect',
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    /** strings or { value, text, disabled } */
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    error: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
  },
  computed: {
    normalizedOptions() {
      return this.options.map(opt => {
        if (opt !== null && typeof opt === 'object') {
          return { value: opt.value, text: opt.text != null ? opt.text : opt.value, disabled: !!opt.disabled }
        }
        return { value: opt, text: opt, disabled: false }
      })
    },
    fieldListeners() {
      return {
        ...this.$listeners,
        change: e => this.$emit('change', e.target.value),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-select {
  position: relative;
  display: block;

  &__field {
    display: block;
    width: 100%;
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--text-strong);
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius);
    box-shadow: var(--shadow-hard-sm);
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    transition:
      box-shadow var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);

    &:focus {
      outline: none;
      box-shadow: var(--shadow-hard);
    }
  }

  &__caret {
    position: absolute;
    top: 50%;
    right: 14px;
    display: inline-flex;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--text-strong);

    svg {
      width: 18px;
      height: 18px;
      fill: none;
      stroke: currentColor;
      stroke-width: 3;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  /* ── Sizes ── */
  &--sm .play-select__field { padding: 9px 38px 9px 13px;  font-size: 14px; border-radius: var(--radius-sm); }
  &--md .play-select__field { padding: 12px 40px 12px 15px; font-size: 15px; }
  &--lg .play-select__field { padding: 15px 44px 15px 18px; font-size: 16px; border-radius: var(--radius-lg); }

  /* ── Invalid ── */
  &.is-error .play-select__field {
    border-color: var(--c-danger);
    box-shadow: 0 4px 0 var(--c-danger-strong);

    &:focus { box-shadow: 0 6px 0 var(--c-danger-strong); }
  }

  /* ── Disabled ── */
  &.is-disabled {
    opacity: .55;

    .play-select__field { pointer-events: none; }
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-select__field { transition: none; }
}
</style>
